import { useEffect, useRef, useState } from "react";
import * as s from "./report.styles";
import styled, { css } from "styled-components";
import { transNum } from "../../../component/function/function";
import BarGraph from "./BarGraph";
import { ReportTractorEfficiency } from "../machine";

const tabList = ["요약", "작업별 최적화", "최대 동시작업", "작업 성능 진단"];
type tabId = (typeof tabList)[number];

const HEADER_OFFSET = 110;
interface Props {
  scrollContainerRef?: React.RefObject<HTMLElement>;
  data: ReportTractorEfficiency;
  hideInnerTabs?: boolean;
}

function TractorEfficiency({ scrollContainerRef, data, hideInnerTabs }: Props) {
  const [activeTab, setActiveTab] = useState("요약");

  const containerRef = useRef<HTMLDivElement | null>(null);
  const summaryRef = useRef<HTMLDivElement | null>(null);
  const taskOptRef = useRef<HTMLDivElement | null>(null);
  const concurrentRef = useRef<HTMLDivElement | null>(null);
  const performanceRef = useRef<HTMLDivElement | null>(null);

  const tabToRef: Record<string, React.RefObject<HTMLDivElement>> = {
    요약: summaryRef,
    "작업별 최적화": taskOptRef,
    "최대 동시작업": concurrentRef,
    "작업 성능 진단": performanceRef,
  };

  const handleClickTab = (id: tabId) => {
    setActiveTab(id);
    const target = tabToRef[id].current;
    const scrollRoot = scrollContainerRef?.current || window; // ref가 있으면 그 안에서, 없으면 window

    if (!target) return;

    if (scrollRoot instanceof Window) {
      const top =
        target.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET; // 56 = 상단 탭/헤더 높이 보정
      window.scrollTo({ top, behavior: "smooth" });
    } else {
      const rootTop = scrollRoot.getBoundingClientRect().top;
      const targetTop = target.getBoundingClientRect().top;
      const offset = targetTop - rootTop - HEADER_OFFSET; // 스크롤 컨테이너 기준 오프셋
      scrollRoot.scrollTo({
        top: scrollRoot.scrollTop + offset,
        behavior: "smooth",
      });
    }
  };

  const getStatus = (current: number, required: number) => {
    if (1 - current / required <= 0) return "적정";
    if (1 - current / required <= 0.2) return "다소 부족"
    if (1 - current / required > 0.2) return "부족";
  };

  const maxValue = Math.max(
    ...data.tasks.map((t) => Math.max(t.capacity, t.requiredCapacity))
  );

  // 섹션 스크롤에 따라 탭 active 변경
  useEffect(() => {
    const rootEl = scrollContainerRef?.current || null;
    const scrollTarget: HTMLElement | Window = rootEl || window;

    const handleScroll = () => {
      const sections: [tabId, HTMLDivElement | null][] = [
        ["요약", summaryRef.current],
        ["작업별 최적화", taskOptRef.current],
        ["최대 동시작업", concurrentRef.current],
        ["작업 성능 진단", performanceRef.current],
      ];

      let currentId: tabId = "요약";
      let currentDistance = -Infinity;

      const rootTop = rootEl ? rootEl.getBoundingClientRect().top : 0;

      sections.forEach(([id, el]) => {
        if (!el) return;
        const rect = el.getBoundingClientRect();

        // 섹션 top 이 "루트 상단 + 헤더 높이"에서 얼마나 떨어져 있는지
        const distance = rect.top - rootTop - HEADER_OFFSET;

        // distance <= 0 이면 헤더 아래를 이미 지나간 섹션
        // 그 중에서 가장 0에 가까운(최근에 지나간) 섹션을 active 로
        if (distance <= 0 && distance > currentDistance) {
          currentDistance = distance;
          currentId = id;
        }
      });

      setActiveTab(currentId);
    };

    // 처음 렌더될 때도 한 번 계산
    handleScroll();

    // 스크롤 타겟에 이벤트 등록
    scrollTarget.addEventListener("scroll", handleScroll);
    return () => {
      scrollTarget.removeEventListener("scroll", handleScroll);
    };
  }, [scrollContainerRef]);

  return (
    <s.Container ref={containerRef}>
      {!hideInnerTabs && (
        <s.TabContainer>
          {tabList.map((t) => (
            <s.Tab
              key={t}
              $active={activeTab === t}
              onClick={() => handleClickTab(t as tabId)}
            >
              {t}
            </s.Tab>
          ))}
        </s.TabContainer>
      )}
      <s.Section ref={summaryRef}>
        <s.SectionTitle>트랙터/부속기 최적화 요약</s.SectionTitle>
        <SummaryContainer>
          <SummaryItem>
            <div className="label">보유 트랙터</div>
            <div className="value">
              {data.summary.tractorNum}대
              <SummaryStatus
                $status={getStatus(
                  data.summary.tractorNum,
                  data.summary.requiredTractorNum
                )}
              >
                {getStatus(
                  data.summary.tractorNum,
                  data.summary.requiredTractorNum
                )}
              </SummaryStatus>
            </div>
          </SummaryItem>
          <SummaryItem>
            <div className="label">권장 트랙터</div>
            <div className="value">{data.summary.requiredTractorNum}대</div>
          </SummaryItem>
          <SummaryItem>
            <div className="label">보유 부속기</div>
            <div className="value">
              {data.summary.implementNum}대
              <SummaryStatus
                $status={getStatus(
                  data.summary.implementNum,
                  data.summary.requiredImplementNum
                )}
              >
                {getStatus(
                  data.summary.implementNum,
                  data.summary.requiredImplementNum
                )}
              </SummaryStatus>
            </div>
          </SummaryItem>
          <SummaryItem>
            <div className="label">권장 부속기</div>
            <div className="value">{data.summary.requiredImplementNum}대</div>
          </SummaryItem>
        </SummaryContainer>
      </s.Section>

      <s.Section ref={taskOptRef}>
        <s.SectionTitle>작업별 최적 부속기</s.SectionTitle>
        <TaskList>
          {data.tasks.map((task) => (
            <TaskItem key={task.name}>
              <TaskItemHeader>
                <div className="title">{task.name} 작업 능력</div>
                <TaskItemLegends>
                  <TaskItemLegend $isRequired>
                    <div className="dot" />
                    필요 능력
                  </TaskItemLegend>
                  <TaskItemLegend>
                    <div className="dot" />
                    보유 능력
                  </TaskItemLegend>
                </TaskItemLegends>
              </TaskItemHeader>
              <BarGraph
                required={task.requiredCapacity}
                actual={Number(task.capacity.toFixed(3))}
                maxValue={maxValue}
                unit="ha/일"
              />
            </TaskItem>
          ))}
        </TaskList>
        <CardList>
          {data.tasks.map((task) => (
            <Card key={task.name}>
              <CardHeader>
                <CardHeaderDesc>{task.name}</CardHeaderDesc>
                <CardTitle>{task.implement}</CardTitle>
              </CardHeader>
              {task.units === 0 ? (
                <CardContent>
                  <CardContentItem>
                    <div className="label">보유대수</div>
                    <div className="value number">없음</div>
                    <SummaryStatus
                      $status={getStatus(task.units, task.requiredUnits)}
                    >
                      {getStatus(task.units, task.requiredUnits)}
                    </SummaryStatus>
                  </CardContentItem>
                </CardContent>
              ) : (
                <CardContent>
                  <CardContentItem>
                    <div className="label">보유대수</div>
                    <div className="value number">{task.units}대</div>
                    <SummaryStatus
                      $status={getStatus(task.units, task.requiredUnits)}
                    >
                      {getStatus(task.units, task.requiredUnits)}
                    </SummaryStatus>
                  </CardContentItem>
                  <CardContentItem>
                    <div className="label">작업기간</div>
                    <div className="value">{task.periodDays}일/월</div>
                  </CardContentItem>
                  <CardContentItem>
                    <div className="label">속도</div>
                    <div className="value">{task.speed}km/h</div>
                  </CardContentItem>
                  <CardContentItem>
                    <div className="label">사용폭</div>
                    <div className="value">{task.width}m</div>
                  </CardContentItem>
                </CardContent>
              )}
              <CardSummary>
                <CardSummaryItem>
                  <div className="label">권장대수</div>
                  <div className="value number">{task.requiredUnits}대</div>
                </CardSummaryItem>
                <CardSummaryItem>
                  <div className="label">1대당 작업 능력</div>
                  <div className="value ">{task.capacity.toFixed(3)}ha/일</div>
                </CardSummaryItem>
                <CardSummaryItem>
                  <div className="label">필요능력</div>
                  <div className="value ">
                    {task.requiredCapacity.toFixed(3)}ha/일
                  </div>
                </CardSummaryItem>
              </CardSummary>
            </Card>
          ))}
        </CardList>
      </s.Section>

      <s.Section ref={concurrentRef}>
        <s.SectionTitle>최대 동시작업</s.SectionTitle>
        <ConCurrent>
          <SummaryItem>
            <div className="label">경운/정지</div>
            <div className="value">{data.groupTasks.group1}대</div>
          </SummaryItem>
          <SummaryItem>
            <div className="label">하역/운반</div>
            <div className="value">{data.groupTasks.group2}대</div>
          </SummaryItem>
          <SummaryItem>
            <div className="label">시비/방제</div>
            <div className="value">{data.groupTasks.group3}대</div>
          </SummaryItem>
        </ConCurrent>
      </s.Section>

      <s.Section ref={performanceRef}>
        <s.SectionTitle>작업 성능 진단</s.SectionTitle>
        <PerformanceList>
          {data.test.map((item) => {
            const unit = item.name.includes("작업") ? "(대)" : "(ha/일)";
            const recommend = item.name.includes("작업")
              ? item.recommendUnits
              : item.recommendCapacity;
            const now = item.name.includes("작업") ? item.units : item.capacity;
            const status = recommend > now ? "부족" : "적정";

            return (
              <PerformanceItem key={item.name}>
                <PerformanceItemHeader>
                  <div className="item-title">{item.name}</div>
                  <div className="status">{status}</div>
                </PerformanceItemHeader>
                <PerformanceItemContent>
                  <div className="label">권장 | 보유</div>
                  <div className="value">
                    {recommend ?? 0} | {now ?? 0}
                    {unit}
                  </div>
                </PerformanceItemContent>
              </PerformanceItem>
            );
          })}
        </PerformanceList>
      </s.Section>
    </s.Container>
  );
}

export default TractorEfficiency;

const SummaryContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  align-self: stretch;
`;

const SummaryItem = styled.div`
  padding: 12px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
  flex: 1 0 0;
  border-radius: 8px;
  background: var(--Alias-Color-Text-White-Gray-LightGray-100, #f7f8f9);

  & > .label {
    color: var(--Alias-Color-Text-Dark-Text-Secondary, rgba(23, 30, 38, 0.78));
    font-size: 14px;
    font-weight: 500;
    line-height: 20px; /* 142.857% */
  }

  & > .value {
    display: flex;
    align-items: center;
    gap: 4px;
    align-self: stretch;
    color: var(--Alias-Color-Text-Dark-Text-Primary, #171e26);
    font-size: 18px;
    font-weight: 700;
    line-height: 165%; /* 29.7px */
    letter-spacing: -0.18px;
  }
`;

const SummaryStatus = styled.div<{ $status?: string }>`
  color: ${({ $status }) =>
    $status === "충분"
      ? "#0C64CE"
      : $status === "부족"
        ? "#E78B3E"
        : "#0E815B"};
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px; /* 142.857% */
`;

const TaskList = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const TaskItem = styled.div`
  display: flex;
  padding: 8px 0 16px 0;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  align-self: stretch;
`;

const TaskItemHeader = styled.div`
  display: flex;
  padding: 4px 0;
  align-items: center;
  gap: 2px;
  align-self: stretch;

  & > .title {
    flex: 1 0 0;
    color: var(--Alias-Color-Text-Dark-Text-Secondary, rgba(23, 30, 38, 0.78));
    font-family: Pretendard;
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 150%; /* 24px */
  }
`;

const TaskItemLegends = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
`;

const TaskItemLegend = styled.div<{ $isRequired?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;

  color: rgba(23, 30, 38, 0.78);
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px; /* 142.857% */

  & > .dot {
    width: 8px;
    height: 8px;
    border-radius: 9999px;
    background-color: #1e9b6b;
  }

  ${({ $isRequired }) =>
    $isRequired &&
    css`
      color: rgba(23, 30, 38, 0.56);

      & > .dot {
        background-color: #8de2c2;
      }
    `}
`;

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

const Card = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  border-radius: 8px;
  background: var(--Alias-Color-Text-White-Gray-LightGray-100, #f7f8f9);
`;

const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
`;

const CardHeaderDesc = styled.div`
  color: var(--Alias-Color-Text-Dark-Text-Secondary, rgba(23, 30, 38, 0.78));
  font-feature-settings:
    "liga" off,
    "clig" off;

  /* Mo/Body/L-16-Medium */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 24px */
`;

const CardTitle = styled.div`
  color: var(--Alias-Color-Text-Dark-Text-Primary, #171e26);

  /* Web/Body/L-18-Bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 165%; /* 29.7px */
  letter-spacing: -0.18px;
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;

const CardContentItem = styled.div`
  display: flex;
  padding: 8px 0;
  align-items: center;
  gap: 4px;
  align-self: stretch;

  & > .label {
    color: var(--Alias-Color-Text-Dark-Text-Secondary, rgba(23, 30, 38, 0.78));
    font-feature-settings:
      "liga" off,
      "clig" off;

    /* Mo/Body/S-14-Medium */
    font-family: Pretendard;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px; /* 142.857% */
  }

  & > .value {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-end;
    gap: 8px;
    flex: 1 0 0;
    color: var(--Alias-Color-Text-Dark-Text-Primary, #171e26);
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 150%; /* 24px */
  }

  & > .value.number {
    font-weight: 700;
  }
`;

const CardSummary = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
`;

const CardSummaryItem = styled(CardContentItem)`
  display: flex;
  padding: 8px 0;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  flex: 1 0 0;
`;

const ConCurrent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

const PerformanceList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  align-self: stretch;
`;

const PerformanceItem = styled.div`
  display: flex;
  padding: 12px 0;
  gap: 4px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;
  border-bottom: 1px solid
    var(--Alias-Color-Line-Outline, rgba(23, 30, 38, 0.1));
`;

const PerformanceItemHeader = styled.div`
  display: flex;
  align-items: center;
  align-self: stretch;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px; /* 142.857% */

  & > .item-title {
    color: var(--Alias-Color-Text-Dark-Text-Secondary, rgba(23, 30, 38, 0.78));
  }

  & > .status {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 4px;
    flex: 1 0 0;
    color: var(--Alias-Color-Text-Dark-Text-Primary, #171e26);
  }
`;

const PerformanceItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: stretch;

  & > .label {
    display: flex;
    align-items: center;
    gap: 4px;
    color: var(--Alias-Color-Text-Dark-Text-Tertiary, rgba(23, 30, 38, 0.56));
    font-feature-settings:
      "liga" off,
      "clig" off;

    /* Mo/Body/S-14-Medium */
    font-family: Pretendard;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px; /* 142.857% */
  }

  & > .value {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 4px;
    flex: 1 0 0;
    color: var(--Alias-Color-Text-Dark-Text-Secondary, rgba(23, 30, 38, 0.78));
    text-align: right;
    font-feature-settings:
      "liga" off,
      "clig" off;

    /* Mo/Body/S-14-Medium */
    font-family: Pretendard;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px; /* 142.857% */
  }
`;
